// src/pages/SucursalEditor.js
//
// Editor de una sucursal. Tres tabs:
//  - Datos generales: nombre, código, teléfono, correo.
//  - Ubicación: dirección + país/depto/municipio + mapa Leaflet
//    con pin arrastrable y búsqueda por dirección (Nominatim).
//  - Presencia: switches para activar en POS Compacto y en HolaVeci,
//    con validación de requisitos.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Tabs, Tab, TextField, Grid,
  Button, Alert, CircularProgress, MenuItem, Divider, Chip, Switch,
  FormControlLabel, Autocomplete, IconButton, Tooltip,
} from '@mui/material';
import SaveIcon         from '@mui/icons-material/Save';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';
import StorefrontIcon   from '@mui/icons-material/Storefront';
import PlaceIcon        from '@mui/icons-material/Place';
import ToggleOnIcon     from '@mui/icons-material/ToggleOn';
import StarIcon         from '@mui/icons-material/Star';
import { useApp } from '../context/AppContext';
import { sucursales as apiSucursales, ubicacion } from '../api/posApi';
import MapaSucursal from '../components/MapaSucursal';

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 3 }}>{value === index && children}</Box>
);

const SucursalEditor = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { empresa, refrescarEmpresa, notify } = useApp();

  const [tab, setTab]           = useState(0);
  const [suc, setSuc]           = useState(null);
  const [form, setForm]         = useState(null);
  const [dirty, setDirty]       = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');

  // Combos ubicación
  const [departamentos, setDeptos]     = useState([]);
  const [municipios, setMunicipios]    = useState([]);
  const [deptoSel, setDeptoSel]        = useState(null);

  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  // ─── Cargar sucursal + deptos ───
  useEffect(() => {
    let cancelado = false;
    (async () => {
      setCargando(true); setError('');
      try {
        const s = await apiSucursales.obtener(id);
        if (cancelado) return;
        setSuc(s);
        setForm({
          codigo:      s.codigo || s.Codigo || '',
          nombre:      s.nombre || s.Nombre || '',
          nombreCorto: s.nombreCorto || s.NombreCorto || '',
          direccion:   s.direccion || s.Direccion || '',
          municipioID: s.municipioID || s.MunicipioID || null,
          telefono:    s.telefono || s.Telefono || '',
          email:       s.email    || s.Email    || '',
          esPrincipal: s.esPrincipal ?? s.EsPrincipal ?? false,
          latitud:     s.latitud  ?? s.Latitud  ?? null,
          longitud:    s.longitud ?? s.Longitud ?? null,
          enHolaVeci:    s.enHolaVeci    ?? s.EnHolaVeci    ?? false,
          enPosCompacto: s.enPosCompacto ?? s.EnPosCompacto ?? false,
        });
        setDirty(false);

        // Cargar deptos (Colombia)
        const paises = await ubicacion.paises();
        const colombia = (Array.isArray(paises) ? paises : (paises.items || []))
          .find(p => (p.nombre || p.Nombre || '').toLowerCase().includes('colombia')) || (paises[0]);
        if (colombia) {
          const paisId = colombia.paisId || colombia.PaisId || colombia.id || colombia.Id;
          const dep = await ubicacion.departamentos(paisId);
          if (!cancelado) setDeptos(Array.isArray(dep) ? dep : (dep.items || []));
        }

        // Si la sucursal tiene municipio, cargamos su depto y sus municipios
        const mid = s.municipioID || s.MunicipioID;
        if (mid) {
          // Buscamos el municipio para inferir su depto
          try {
            const munBuscados = await ubicacion.municipios(undefined, s.municipioNombre || s.MunicipioNombre || '');
            const found = (Array.isArray(munBuscados) ? munBuscados : []).find(
              m => (m.municipioID || m.MunicipioID) === mid
            );
            if (found) {
              const deptoId = found.departamentoId || found.DepartamentoId
                          || found.departamentoID || found.DepartamentoID;
              if (deptoId) {
                setDeptoSel(deptoId);
                const munDelDepto = await ubicacion.municipios(deptoId);
                if (!cancelado) setMunicipios(Array.isArray(munDelDepto) ? munDelDepto : []);
              }
            }
          } catch { /* silent */ }
        }
      } catch (e) {
        if (!cancelado) setError(e.mensajeUsuario || 'No se pudo cargar la sucursal.');
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => { cancelado = true; };
  }, [id]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const onCoordsChange = useCallback((lat, lng) => {
    setForm((f) => ({ ...f, latitud: lat, longitud: lng }));
    setDirty(true);
  }, []);

  const cambiarDepto = async (deptoId) => {
    setDeptoSel(deptoId);
    set('municipioID', null);
    if (!deptoId) { setMunicipios([]); return; }
    try {
      const r = await ubicacion.municipios(deptoId);
      setMunicipios(Array.isArray(r) ? r : []);
    } catch { setMunicipios([]); }
  };

  // ─── Validación de requisitos para HolaVeci ───
  const requisitosHolaVeci = () => {
    const faltantes = [];
    if (!form?.nombre)     faltantes.push('nombre');
    if (!form?.direccion)  faltantes.push('dirección');
    if (form?.latitud == null || form?.longitud == null) faltantes.push('coordenadas');
    if (!form?.municipioID) faltantes.push('municipio');
    return faltantes;
  };

  const toggleHolaVeci = () => {
    // Al ACTIVAR requiere requisitos completos. Al DESACTIVAR siempre pasa.
    if (!form.enHolaVeci) {
      const faltan = requisitosHolaVeci();
      if (faltan.length > 0) {
        notify(`Para activar en HolaVeci completa: ${faltan.join(', ')}.`, 'warning');
        return;
      }
    }
    set('enHolaVeci', !form.enHolaVeci);
  };

  const togglePOS = () => set('enPosCompacto', !form.enPosCompacto);

  const guardar = async () => {
    if (!form) return;
    // Regla: al menos un flag activo
    if (!form.enHolaVeci && !form.enPosCompacto) {
      if (!window.confirm(
        'Esta sucursal quedará SIN presencia en ninguna app. No aparecerá ' +
        'en HolaVeci ni en HolaVeci Local hasta que reactives alguno. ¿Continuar?'
      )) return;
    }
    setGuardando(true); setError('');
    try {
      await apiSucursales.actualizar(id, {
        empresaId,
        codigo:        form.codigo,
        nombre:        form.nombre,
        nombreCorto:   form.nombreCorto || null,
        esPrincipal:   form.esPrincipal,
        direccion:     form.direccion,
        municipioID:   form.municipioID,
        telefono:      form.telefono || null,
        email:         form.email    || null,
        latitud:       form.latitud,
        longitud:      form.longitud,
        enHolaVeci:    form.enHolaVeci,
        enPosCompacto: form.enPosCompacto,
      });
      await refrescarEmpresa();
      notify('Sucursal actualizada.', 'success');
      setDirty(false);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo guardar.');
    } finally { setGuardando(false); }
  };

  if (cargando) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }
  if (!form) {
    return <Alert severity="error">{error || 'No se pudo cargar la sucursal.'}</Alert>;
  }

  const nombreEmpresa = empresa?.nombreComercial || empresa?.NombreComercial
                    || empresa?.razonSocial || empresa?.RazonSocial;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/sucursales')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <StorefrontIcon color="primary" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {form.nombre || 'Sucursal'} {form.esPrincipal && (
              <Tooltip title="Sucursal principal">
                <StarIcon fontSize="small" color="warning" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
              </Tooltip>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {nombreEmpresa}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          {form.enHolaVeci    && <Chip size="small" label="HolaVeci" color="info"    variant="filled" />}
          {form.enPosCompacto && <Chip size="small" label="Local"    color="primary" variant="filled" />}
          {!form.enHolaVeci && !form.enPosCompacto &&
            <Chip size="small" label="Sin actividad" variant="outlined" />}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
              variant="scrollable" scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<StorefrontIcon />} iconPosition="start" label="Datos generales" />
          <Tab icon={<PlaceIcon />}      iconPosition="start" label="Ubicación" />
          <Tab icon={<ToggleOnIcon />}   iconPosition="start" label="Presencia" />
        </Tabs>

        <CardContent>
          {/* ─── DATOS GENERALES ─── */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Código" value={form.codigo}
                           onChange={(e) => set('codigo', e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth label="Nombre" value={form.nombre}
                           onChange={(e) => set('nombre', e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Nombre corto" value={form.nombreCorto}
                           onChange={(e) => set('nombreCorto', e.target.value)}
                           helperText="Opcional. Para el header cuando el nombre completo es largo." />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={form.esPrincipal}
                    onChange={(e) => set('esPrincipal', e.target.checked)} />}
                  label="Sucursal principal"
                />
              </Grid>
              <Grid item xs={12}><Divider><Chip label="Contacto" size="small" /></Divider></Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Teléfono" value={form.telefono}
                           onChange={(e) => set('telefono', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Correo" type="email" value={form.email}
                           onChange={(e) => set('email', e.target.value)} />
              </Grid>
            </Grid>
          </TabPanel>

          {/* ─── UBICACIÓN ─── */}
          <TabPanel value={tab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Dirección" value={form.direccion}
                           onChange={(e) => set('direccion', e.target.value)}
                           required
                           helperText="Cra 27 #52-33, Barrio Cabecera" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Departamento"
                           value={deptoSel || ''}
                           onChange={(e) => cambiarDepto(e.target.value || null)}>
                  <MenuItem value="">(sin especificar)</MenuItem>
                  {departamentos.map((d) => {
                    const did = d.departamentoId || d.DepartamentoId || d.departamentoID || d.DepartamentoID;
                    return (
                      <MenuItem key={did} value={did}>
                        {d.nombre || d.Nombre}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={municipios}
                  getOptionLabel={(m) => m ? (m.nombre || m.Nombre || '') : ''}
                  value={municipios.find(m => (m.municipioID || m.MunicipioID) === form.municipioID) || null}
                  onChange={(_, v) => set('municipioID', v ? (v.municipioID || v.MunicipioID) : null)}
                  disabled={!deptoSel}
                  renderInput={(params) => (
                    <TextField {...params} label="Municipio"
                               helperText={!deptoSel ? 'Elige departamento primero' : ''}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}><Divider><Chip label="Mapa" size="small" icon={<PlaceIcon />} /></Divider></Grid>
              <Grid item xs={12}>
                <MapaSucursal
                  lat={form.latitud}
                  lng={form.longitud}
                  onCoordsChange={onCoordsChange}
                  notify={notify}
                  municipioNombre={
                    municipios.find(m => (m.municipioID || m.MunicipioID) === form.municipioID)?.nombre
                    || municipios.find(m => (m.municipioID || m.MunicipioID) === form.municipioID)?.Nombre
                  }
                  departamentoNombre={
                    departamentos.find(d => (d.departamentoId || d.DepartamentoId || d.departamentoID || d.DepartamentoID) === deptoSel)?.nombre
                    || departamentos.find(d => (d.departamentoId || d.DepartamentoId || d.departamentoID || d.DepartamentoID) === deptoSel)?.Nombre
                  }
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* ─── PRESENCIA ─── */}
          <TabPanel value={tab} index={2}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Elige en qué apps opera esta sucursal. Puedes activarla en una,
              en las dos, o desactivarla temporalmente. Los datos históricos
              siempre se conservan.
            </Typography>

            <Stack spacing={2}>
              <Card variant="outlined" sx={{
                borderColor: form.enHolaVeci ? 'info.main' : 'divider',
                borderWidth: form.enHolaVeci ? 2 : 1,
              }}>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" spacing={2}>
                    <Chip label="HolaVeci" color="info" variant={form.enHolaVeci ? 'filled' : 'outlined'} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Directorio del barrio
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        La sucursal aparece en el mapa de HolaVeci. Los vecinos
                        pueden hacer pedidos, ver el catálogo y usar fiado.
                      </Typography>
                      {!form.enHolaVeci && requisitosHolaVeci().length > 0 && (
                        <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                          Para activar necesitas completar: <b>{requisitosHolaVeci().join(', ')}</b>.
                        </Alert>
                      )}
                    </Box>
                    <Switch
                      checked={form.enHolaVeci}
                      onChange={toggleHolaVeci}
                      color="info"
                    />
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{
                borderColor: form.enPosCompacto ? 'primary.main' : 'divider',
                borderWidth: form.enPosCompacto ? 2 : 1,
              }}>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" spacing={2}>
                    <Chip label="HolaVeci Local" color="primary" variant={form.enPosCompacto ? 'filled' : 'outlined'} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Punto de venta e inventario
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Habilita facturación DIAN, apertura/cierre de caja,
                        inventario, catálogo y ventas para esta sucursal.
                      </Typography>
                    </Box>
                    <Switch
                      checked={form.enPosCompacto}
                      onChange={togglePOS}
                      color="primary"
                    />
                  </Stack>
                </CardContent>
              </Card>

              {!form.enHolaVeci && !form.enPosCompacto && (
                <Alert severity="warning">
                  Sin ninguna app activa, esta sucursal no aparecerá en ningún
                  lado. Puedes conservarla así si quieres archivarla
                  temporalmente sin borrarla.
                </Alert>
              )}
            </Stack>
          </TabPanel>

          <Divider sx={{ my: 3 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={() => navigate('/sucursales')} disabled={guardando}>
              Cerrar
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />}
                    onClick={guardar} disabled={!dirty || guardando}>
              {guardando ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SucursalEditor;
